import { useState, type FormEvent } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { submitApplication } from "@/api/applications";
import { BrandMark } from "@/components/BrandMark";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";

export function ApplyPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [rpps, setRpps] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      submitApplication({
        last_name: lastName.trim(),
        first_name: firstName.trim() || undefined,
        rpps: rpps.trim() || undefined,
        email: email.trim().toLowerCase(),
        phone: phone.trim() || undefined,
        organization_code: orgCode.trim().toLowerCase(),
        message: message.trim() || undefined,
      }),
    onSuccess: () => setDone(true),
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response
        ?.data?.detail;
      setError(typeof detail === "string" ? detail : "submit_failed");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!lastName.trim() || !email.trim() || !orgCode.trim()) {
      setError("missing_required");
      return;
    }
    mutation.mutate();
  };

  const bg = theme.palette.mode === "dark" ? "/brand/bg-dark.svg" : "/brand/bg-light.svg";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 2,
        py: 4,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: "absolute", top: 16, right: 16 }}
      >
        <LocaleSwitcher />
        <ThemeSwitcher />
      </Stack>

      <Card
        sx={{
          width: "100%",
          maxWidth: 540,
          backdropFilter: "blur(8px)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(30, 30, 30, 0.88)"
              : "rgba(255, 255, 255, 0.94)",
          borderColor: "divider",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack alignItems="center" spacing={1.5}>
              <BrandMark variant="wordmark" height={56} />
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                {t("apply.title")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {t("apply.subtitle")}
              </Typography>
            </Stack>

            {done ? (
              <Stack spacing={2}>
                <Alert severity="success">{t("apply.done")}</Alert>
                <Button
                  variant="contained"
                  onClick={() => navigate("/login", { replace: true })}
                >
                  {t("apply.goToLogin")}
                </Button>
              </Stack>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                <Stack spacing={2}>
                  {error && (
                    <Alert severity="error">
                      {t(`apply.err.${error}`, { defaultValue: error })}
                    </Alert>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label={t("apply.lastName")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label={t("apply.firstName")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <TextField
                    label={t("apply.rpps")}
                    value={rpps}
                    onChange={(e) => setRpps(e.target.value.replace(/\s+/g, ""))}
                    helperText={t("apply.rppsHint")}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 11 }}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label={t("apply.email")}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label={t("apply.phone")}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <TextField
                    label={t("apply.orgCode")}
                    value={orgCode}
                    onChange={(e) => setOrgCode(e.target.value)}
                    helperText={t("apply.orgCodeHint")}
                    required
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label={t("apply.message")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    minRows={2}
                    maxRows={5}
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={mutation.isPending}
                    sx={{ py: 1.2 }}
                  >
                    {mutation.isPending ? t("common.loading") : t("apply.submit")}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {t("apply.alreadyHaveAccount")}{" "}
                    <Box
                      component={RouterLink}
                      to="/login"
                      sx={{ color: "primary.main", textDecoration: "none" }}
                    >
                      {t("apply.signInHere")}
                    </Box>
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
